import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import User from '../../models/User'

import { IPublicBeneficiaryResponse, medicareApiClient } from '../../clients/medicareApiClient'
import { RootState } from 'redux/config'
import { slsClient } from 'clients/slsClient'
import { Logger } from 'utils/logger'

/**
 * @deprecated Being phased out with feature flag use-only-sls-jwt-for-auth-by-default
 */
export const fetchReqTokenAndUser = createAsyncThunk(
  'users/fetchReqTokenAndUser',
  async (_: undefined, { dispatch, rejectWithValue }) => {
    try {
      const { req_token: slsRequestToken } = await slsClient.getReqToken()
      if (slsRequestToken) {
        // This call gets us our MAX token
        const publicBeneficiary = await medicareApiClient.asyncGetBeneficiaryByReqToken(slsRequestToken)
        dispatch(setUser(publicBeneficiary))
        return Boolean(publicBeneficiary)
      } else {
        throw Error('no slsRequestToken, see fetchUser thunk')
      }
    } catch (error) {
      Logger.error(error)
      return rejectWithValue(undefined)
    }
  }
)

/**
 * Use to fetch user by calling the api /verify followed by /beneficiary endpoint
 */
export const fetchUser = createAsyncThunk(
  'users/fetchUser',
  async (_: undefined, { dispatch, rejectWithValue }) => {
    try {
      const verifyResponse = await medicareApiClient.asyncVerify()
      const { authenticated } = verifyResponse
      if (!authenticated) {
        return rejectWithValue(undefined)
      }

      const beneficiary = await medicareApiClient.beneficiaryApi.getBeneficiary()
      dispatch(setUser(beneficiary.data))
      return Boolean(beneficiary.data)
    } catch (error) {
      Logger.error(error)
      return rejectWithValue(undefined)
    }
  }
)

interface IUserReducer {
  user?: User
  status: 'finished' | 'error' | 'loading' | 'idle'
}

type ISetUserAction = IPublicBeneficiaryResponse

const initialState: IUserReducer = {
  user: undefined,
  status: 'idle'
}

export const userSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setUser: (state: IUserReducer, action: PayloadAction<ISetUserAction>) => {
      state.user = action.payload
    },
    clearUser: (state: IUserReducer) => {
      state.user = initialState.user
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(fetchUser.fulfilled, (state) => {
        state.status = 'finished'
      })
      .addCase(fetchUser.rejected, (state) => {
        state.user = initialState.user
        state.status = 'error'
      })
      .addCase(fetchReqTokenAndUser.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(fetchReqTokenAndUser.fulfilled, (state) => {
        state.status = 'finished'
      })
      .addCase(fetchReqTokenAndUser.rejected, (state) => {
        state.user = initialState.user
        state.status = 'error'
      })
  }
})

export const {
  setUser,
  clearUser
} = userSlice.actions

// GETTERS
export const getUser = (state: RootState) => state.users.user
export const getUserLoggedInState = (state: RootState) => Boolean(state.users.user)
export const getUserStatus = (state: RootState) => state.users.status

export default userSlice.reducer
